// GalleryTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { formatDistanceToNow } from 'date-fns';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { 
  Image as ImageIcon, FolderOpen, LayoutGrid, Search, RefreshCw, 
  Sparkles, FileText, TrendingUp, Mail, Bot, Eye, ChevronDown 
} from 'lucide-react';

export const GalleryTab = ({
  galleryDeliverables = [],
  galleryProjects = [],
  galleryStats,
  galleryViewMode,
  onViewModeChange,
  galleryFilter,
  onFilterChange,
  gallerySearch,
  onSearchChange,
  onSearch,
  onRefresh,
  expandedProjects,
  onToggleProject,
  onDeliverableClick,
  onNavigateToDemo
}) => {
  const getDeliverableIcon = (type) => {
    switch(type) {
      case 'image': return <ImageIcon className="h-3 w-3 mr-1" />;
      case 'document': return <FileText className="h-3 w-3 mr-1" />;
      case 'analysis': return <TrendingUp className="h-3 w-3 mr-1" />;
      case 'email': return <Mail className="h-3 w-3 mr-1" />;
      default: return null;
    }
  };

  const getDeliverableBadgeClass = (type) => {
    switch(type) {
      case 'image': return 'bg-pink-100 text-pink-700';
      case 'document': return 'bg-blue-100 text-blue-700';
      case 'analysis': return 'bg-purple-100 text-purple-700';
      case 'email': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <TabsContent value="gallery" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
            <ImageIcon className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-teal-900">Deliverables Gallery</h2>
            <p className="text-teal-700 mt-1">
              Browse all AI-generated content including documents, images, analyses, and more.
            </p>
          </div>
          {galleryStats && (
            <div className="flex gap-3">
              <div className="text-center px-4 py-2 bg-white rounded-lg border border-teal-200">
                <p className="text-2xl font-bold text-teal-700">{galleryStats.total || 0}</p>
                <p className="text-xs text-teal-600">Total</p>
              </div>
              {Object.entries(galleryStats.by_type || {}).slice(0, 3).map(([type, count]) => (
                <div key={type} className="text-center px-3 py-2 bg-white rounded-lg border border-teal-100">
                  <p className="text-lg font-semibold text-teal-600">{count}</p>
                  <p className="text-xs text-muted-foreground capitalize">{type}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <Button
            variant={galleryViewMode === 'projects' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('projects')}
            className={galleryViewMode === 'projects' ? 'bg-teal-600 hover:bg-teal-700' : ''}
          >
            <FolderOpen className="h-4 w-4 mr-1" />
            Projects
          </Button>
          <Button
            variant={galleryViewMode === 'flat' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('flat')}
            className={galleryViewMode === 'flat' ? 'bg-teal-600 hover:bg-teal-700' : ''}
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            All Items
          </Button>
        </div>
        
        <div className="flex-1 min-w-[200px] max-w-[400px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search deliverables..."
              value={gallerySearch}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          {['all', 'document', 'image', 'analysis', 'email', 'task_output'].map((type) => (
            <Button
              key={type}
              variant={galleryFilter === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => onFilterChange(type)}
              className={galleryFilter === type ? 'bg-teal-600 hover:bg-teal-700' : ''}
            >
              {type === 'all' ? 'All' : type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Button>
          ))}
        </div>
        
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Content */}
      {galleryDeliverables.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <ImageIcon className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">No Deliverables Yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Run the AI Demo Mode to generate content.
            </p>
            <Button variant="outline" className="mt-4" onClick={onNavigateToDemo}>
              <Sparkles className="h-4 w-4 mr-2" />
              Go to Demo Mode
            </Button>
          </div>
        </Card>
      ) : galleryViewMode === 'projects' && galleryProjects.length > 0 ? (
        <div className="space-y-6">
          {galleryProjects.map((project) => (
            <Card key={project.id} className="overflow-hidden">
              <div 
                className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-200 p-4 cursor-pointer hover:from-teal-100 hover:to-cyan-100 transition-colors"
                onClick={() => onToggleProject(project.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                      <FolderOpen className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-teal-900">{project.title || 'Untitled Project'}</h3>
                      <p className="text-sm text-teal-700">
                        {project.deliverables_count || project.deliverables?.length || 0} deliverables
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-teal-600 transition-transform ${expandedProjects[project.id] ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              {(expandedProjects[project.id] !== false) && (
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(project.deliverables || []).map((deliverable) => (
                      <div 
                        key={deliverable.id} 
                        className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer group bg-white"
                        onClick={() => onDeliverableClick(deliverable)}
                        data-testid={`deliverable-card-${deliverable.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={getDeliverableBadgeClass(deliverable.type)}>
                            {getDeliverableIcon(deliverable.type)}
                            {deliverable.type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{deliverable.format}</span>
                        </div>

                        {deliverable.type === 'image' && deliverable.base64 ? (
                          <div className="aspect-video bg-slate-100 rounded overflow-hidden mb-2">
                            <img 
                              src={`data:image/png;base64,${deliverable.base64}`} 
                              alt="Generated" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="bg-slate-50 rounded p-2 mb-2 max-h-[80px] overflow-hidden">
                            <p className="text-xs text-muted-foreground line-clamp-3">
                              {deliverable.preview || deliverable.content?.substring(0, 150) || 'No preview'}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Bot className="h-3 w-3" /> {deliverable.agent_name || 'AI'}
                          </span>
                          <Eye className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryDeliverables.map((deliverable) => (
            <Card 
              key={deliverable.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer group relative"
              data-testid={`deliverable-card-flat-${deliverable.id}`}
              onClick={() => onDeliverableClick(deliverable)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <Badge className={getDeliverableBadgeClass(deliverable.type)}>
                    {getDeliverableIcon(deliverable.type)}
                    {deliverable.type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{deliverable.format}</span>
                </div>

                {deliverable.type === 'image' && deliverable.base64 ? (
                  <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden mb-3">
                    <img 
                      src={`data:image/png;base64,${deliverable.base64}`} 
                      alt="Generated" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-3 mb-3 max-h-[120px] overflow-hidden">
                    <p className="text-sm text-muted-foreground line-clamp-4">
                      {deliverable.preview || deliverable.content?.substring(0, 200) || 'No preview available'}
                    </p>
                  </div>
                )}

                <div className="border-t pt-3">
                  <p className="text-sm font-medium truncate">{deliverable.task_title}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Bot className="h-3 w-3" /> {deliverable.agent_name}
                    </span>
                    {deliverable.created_at && (
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(deliverable.created_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                  <div className="bg-white/90 px-3 py-1.5 rounded-md shadow-sm flex items-center gap-1 text-sm font-medium">
                    <Eye className="h-4 w-4" /> View Details
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </TabsContent>
  );
};

export default GalleryTab;
